'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Divider,
  Button,
  Stack,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import Link from 'next/link';
import { Icon } from '@iconify/react';
import ArrowBackIcon from '@/letsgo/icons/ArrowBack';
import { getToolIconForPath } from '@/lib/toolsData';
import { useAiActionCooldown } from '@/hooks/useAiActionCooldown';

const QUERY_HISTORY_KEY = 'floai_hashtag_query_history';
const SAVED_LIST_KEY = 'floai_hashtag_saved_list';
const FEEDBACK_KEY = 'floai_hashtag_feedback';

type SamplePost = { user: string; text: string; url: string };

type HashtagMetrics = {
  tag: string;
  estimatedReach: number;
  weeklyVolume: number;
  trend30d: number[];
  engagementRate: string;
  samplePosts?: SamplePost[];
  dataSource?: 'datamuse_reddit' | 'fallback';
};

type AiCluster = { name: string; tags?: string[] };

type AiResult =
  | { type: 'generate'; result: { caption?: string; hashtags?: string[] } }
  | { type: 'score'; result: { score?: number; explanation?: string } }
  | { type: 'cluster'; result: { clusters?: AiCluster[] } };

function Sparkline({ series }: { series: number[] | undefined }) {
  if (!series || series.length === 0) return null;
  const w = 160;
  const h = 36;
  const pad = 4;
  const max = Math.max(...series, 1);
  const points = series.map((v, i) => {
    const x = pad + (i / Math.max(1, series.length - 1)) * (w - pad * 2);
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} role="img" aria-label="30-day trend">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function HashtagResearchPage() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ tag: string; score: number }[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<HashtagMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [queryHistory, setQueryHistory] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(QUERY_HISTORY_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [savedList, setSavedList] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(SAVED_LIST_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [feedback, setFeedback] = useState<{ worked: string[]; didntWork: string[] }>(() => {
    try {
      const raw = localStorage.getItem(FEEDBACK_KEY);
      return raw ? JSON.parse(raw) : { worked: [], didntWork: [] };
    } catch { return { worked: [], didntWork: [] }; }
  });
  const [contentIdea, setContentIdea] = useState('');
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [suggestSource, setSuggestSource] = useState<'datamuse' | 'mock' | null>(null);
  const hashtagAi = useAiActionCooldown(3500);
  const aiDisabled = hashtagAi.disabled;

  useEffect(() => {
    localStorage.setItem(SAVED_LIST_KEY, JSON.stringify(savedList));
  }, [savedList]);
  useEffect(() => {
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(feedback));
  }, [feedback]);

  const sortedSuggestions = useMemo(() => {
    const workedSet = new Set(feedback.worked || []);
    const didntSet = new Set(feedback.didntWork || []);
    return [...suggestions].sort((a, b) => {
      const aw = workedSet.has(a.tag) ? 1 : 0;
      const bw = workedSet.has(b.tag) ? 1 : 0;
      if (aw !== bw) return bw - aw;
      const ad = didntSet.has(a.tag) ? 1 : 0;
      const bd = didntSet.has(b.tag) ? 1 : 0;
      if (ad !== bd) return ad - bd;
      return b.score - a.score;
    });
  }, [suggestions, feedback]);

  const doSearch = async (q?: string) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setLoading(true);
    setSelected(null);
    setMetrics(null);
    try {
      const res = await fetch('/api/tools/hashtag-research/suggest', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: term }) });
      const json = await res.json();
      if (json.ok) {
        setSuggestions(json.suggestions || []);
        setSuggestSource(json.source === 'mock' ? 'mock' : json.source === 'datamuse' ? 'datamuse' : null);
        const hist = [term, ...queryHistory.filter((t) => t !== term)].slice(0, 10);
        setQueryHistory(hist);
        localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(hist));
      }
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const loadMetrics = async (tag: string) => {
    setSelected(tag);
    setMetrics(null);
    setLoading(true);
    try {
      const res = await fetch('/api/tools/hashtag-research/metrics', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tag }) });
      const json = await res.json();
      if (json.ok) setMetrics(json.metrics);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const addToList = (tag: string) => {
    setSavedList((s) => Array.from(new Set([tag, ...s])));
  };

  const submitFeedback = (tag: string, worked: boolean) => {
    setFeedback((fb) => {
      const workedSet = new Set(fb.worked || []);
      const didntSet = new Set(fb.didntWork || []);
      if (worked) {
        didntSet.delete(tag);
        workedSet.add(tag);
      } else {
        workedSet.delete(tag);
        didntSet.add(tag);
      }
      return { worked: Array.from(workedSet), didntWork: Array.from(didntSet) };
    });
    void fetch('/api/tools/hashtag-research/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, worked }),
    }).catch(() => {});
  };

  const tryInCalculatorLink = (tags: string[]) => {
    const q = encodeURIComponent(tags.map((t) => t.replace(/^#/, '')).join(','));
    return `/tools/social-media-calculator?tags=${q}`;
  };

  const runAi = (type: 'generate' | 'score' | 'cluster') => {
    if (!selected) return;
    void hashtagAi.run(async () => {
      setAiResult(null);
      const res = await fetch('/api/tools/hashtag-research/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, tag: selected, contentIdea: contentIdea || '' }),
      });
      const j = await res.json();
      if (j.ok) setAiResult({ type, result: j.result });
    });
  };

  return (
    <Box component="main" id="main-content" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton component={Link} href="/tools" aria-label="Back to Tools"><ArrowBackIcon /></IconButton>
        <Icon icon={getToolIconForPath('/tools/hashtag-research')} width={34} height={34} />
        <Typography variant="h4">Hashtag Research</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField fullWidth placeholder="Enter a topic or keyword" value={query} onChange={(e) => setQuery(e.target.value)} inputProps={{ 'aria-label': 'Hashtag search' }} />
            <Button variant="contained" onClick={() => doSearch() } disabled={loading}>{loading ? 'Searching...' : 'Search'}</Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {queryHistory.map((h) => (
              <Chip key={h} label={h} onClick={() => { setQuery(h); doSearch(h); }} size="small" />
            ))}
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Suggestions</Typography>
            {suggestSource === 'datamuse' && (
              <Alert severity="info" sx={{ mt: 1 }} variant="outlined">
                Related terms from <MuiLink href="https://www.datamuse.com/api/" target="_blank" rel="noopener noreferrer">Datamuse</MuiLink> (word associations, not official platform hashtag counts).
              </Alert>
            )}
            {suggestSource === 'mock' && (
              <Alert severity="warning" sx={{ mt: 1 }} variant="outlined">
                Live suggestions unavailable; showing pattern-based placeholders.
              </Alert>
            )}
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sortedSuggestions.length === 0 && <Typography color="text.secondary">No suggestions yet. Try a search.</Typography>}
              {sortedSuggestions.map((s) => (
                <Paper key={s.tag} sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Button variant="text" onClick={() => loadMetrics(s.tag)}>#{s.tag}</Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{(s.score*100).toFixed(0)}%</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={() => { navigator.clipboard?.writeText(`#${s.tag}`); }}>Copy</Button>
                    <Button size="small" onClick={() => addToList(s.tag)}>Add</Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Tag Details</Typography>
            {!selected && <Typography color="text.secondary">Select a tag to view details.</Typography>}
            {selected && !metrics && <Typography color="text.secondary">Loading metrics…</Typography>}
            {metrics && (
              <Box>
                <Typography variant="h6">#{metrics.tag}</Typography>
                {metrics.dataSource === 'datamuse_reddit' && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Estimates blend Datamuse word frequency with recent Reddit search results (indicative only).
                  </Typography>
                )}
                {metrics.dataSource === 'fallback' && (
                  <Alert severity="warning" sx={{ mb: 1 }} variant="outlined">
                    Live metrics unavailable; showing demo values.
                  </Alert>
                )}
                <Typography>Estimated Reach: {metrics.estimatedReach}</Typography>
                <Typography>Weekly Volume: {metrics.weeklyVolume}</Typography>
                <Typography>Engagement Rate: {metrics.engagementRate}</Typography>
                <Box sx={{ mt: 1 }}><Sparkline series={metrics.trend30d} /></Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" onClick={() => { addToList(metrics.tag); }}>Add to list</Button>
                  <Button variant="outlined" onClick={() => { navigator.clipboard?.writeText(`#${metrics.tag}`); }}>Copy tag</Button>
                  <Button variant="contained" component={Link} href={tryInCalculatorLink([metrics.tag])}>Try in Calculator</Button>
                </Box>

                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Was this helpful?</Typography>
                  <Button size="small" variant="text" onClick={() => submitFeedback(metrics.tag, true)}>Worked</Button>
                  <Button size="small" variant="text" onClick={() => submitFeedback(metrics.tag, false)}>Didn&apos;t work</Button>
                </Box>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Sample posts</Typography>
                {metrics.samplePosts?.map((p: SamplePost, i: number) => (
                  <Paper key={i} sx={{ p: 1, mt: 1 }}>
                    <Typography variant="caption">{p.user}</Typography>
                    <Typography>{p.text}</Typography>
                    {p.url && p.url !== '#' && (
                      <MuiLink href={p.url} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        View post
                      </MuiLink>
                    )}
                  </Paper>
                ))}

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">AI Tools</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <TextField size="small" placeholder="Optional content idea" value={contentIdea} onChange={(e) => setContentIdea(e.target.value)} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button onClick={() => runAi('generate')} variant="outlined" disabled={aiDisabled} aria-busy={hashtagAi.inFlight}>
                      {hashtagAi.inFlight ? 'Working…' : 'Generate Caption & Hashtags'}
                    </Button>
                    <Button onClick={() => runAi('score')} variant="outlined" disabled={aiDisabled} aria-busy={hashtagAi.inFlight}>
                      {hashtagAi.inFlight ? 'Working…' : 'Predict Performance'}
                    </Button>
                    <Button onClick={() => runAi('cluster')} variant="outlined" disabled={aiDisabled} aria-busy={hashtagAi.inFlight}>
                      {hashtagAi.inFlight ? 'Working…' : 'Cluster Tags'}
                    </Button>
                  </Stack>
                </Stack>

                {aiResult && (
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      {aiResult.type === 'generate' && (
                        <>
                          <Typography variant="subtitle1">Generated caption</Typography>
                          <Typography>{aiResult.result.caption}</Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {(aiResult.result.hashtags || []).map((t: string) => <Chip key={t} label={t} onClick={() => addToList(t.replace(/^#/,''))} />)}
                          </Box>
                        </>
                      )}
                      {aiResult.type === 'score' && (
                        <>
                          <Typography variant="subtitle1">Predicted score</Typography>
                          <Typography>{Math.round((aiResult.result.score||0)*100)}%</Typography>
                          <Typography variant="body2" color="text.secondary">{aiResult.result.explanation}</Typography>
                        </>
                      )}
                      {aiResult.type === 'cluster' && (
                        <>
                          <Typography variant="subtitle1">Clusters</Typography>
                          {(aiResult.result.clusters || []).map((c: AiCluster, idx: number) => (
                            <Box key={idx} sx={{ mt: 1 }}>
                              <Typography variant="caption">{c.name}</Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                {(c.tags||[]).map((t: string) => <Chip key={t} label={t} onClick={() => { addToList(t.replace(/^#/,'')); loadMetrics(t); }} />)}
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
